/**
 * EditorPanel — DOM overlay for the map editor scene.
 * Shows numeric inputs for wavesNum/monstersPerWave/monsterBaseHp
 * and action buttons: Save, Cancel, Reset Override, Clear Waypoints, Remove Last Waypoint.
 */

import type { GameStateView } from "../../application/ports.js";

export interface EditorPanelCallbacks {
  onSave?: () => void;
  onCancel?: () => void;
  onResetOverride?: () => void;
  onClearWaypoints?: () => void;
  onRemoveLastWaypoint?: () => void;
  onSetMode?: (mode: "waypoint" | "block") => void;
  onSetWavesNum?: (n: number) => void;
  onSetMonstersPerWave?: (n: number) => void;
  onSetMonsterBaseHp?: (n: number) => void;
  onExportYaml?: () => void;
}

export interface EditorPanel {
  update(state: GameStateView): void;
  destroy(): void;
}

export function createEditorPanel(
  container: HTMLElement,
  cbs: EditorPanelCallbacks = {}
): EditorPanel {
  const el = document.createElement("div");
  el.className = "editor-panel";
  el.style.cssText = [
    "position:absolute",
    "top:50px",
    "right:10px",
    "width:200px",
    "background:#1a1612",
    "border:1px solid #3a2820",
    "border-radius:6px",
    "padding:12px",
    "color:#e8dcb4",
    "font:13px/1.4 sans-serif",
    "display:none",
    "z-index:100",
  ].join(";");
  container.appendChild(el);

  // Title
  const title = document.createElement("div");
  title.style.cssText = "font-weight:bold;font-size:14px;margin-bottom:10px;color:#d4af37";
  title.textContent = "Настройки поля";
  el.appendChild(title);

  // Helper: label + number input
  function makeNumberInput(
    label: string,
    min: number,
    max: number,
    onChange: (v: number) => void
  ): HTMLInputElement {
    const row = document.createElement("div");
    row.style.cssText = "margin-bottom:8px;display:flex;align-items:center;justify-content:space-between;gap:4px";
    const lbl = document.createElement("label");
    lbl.style.cssText = "flex:1;color:#8a7a5a;font-size:12px";
    lbl.textContent = label;
    const inp = document.createElement("input");
    inp.type = "number";
    inp.min = String(min);
    inp.max = String(max);
    inp.style.cssText = "width:60px;background:#0a0806;border:1px solid #3a2820;color:#e8dcb4;padding:2px 4px;border-radius:3px;font-size:12px";
    inp.addEventListener("change", () => {
      const v = parseInt(inp.value, 10);
      if (!isNaN(v)) onChange(Math.max(min, Math.min(max, v)));
    });
    row.appendChild(lbl);
    row.appendChild(inp);
    el.appendChild(row);
    return inp;
  }

  const wavesInput = makeNumberInput("Волн:", 1, 80, (v) => cbs.onSetWavesNum?.(v));
  const monstersInput = makeNumberInput("Мобов/волна:", 1, 60, (v) => cbs.onSetMonstersPerWave?.(v));
  const hpInput = makeNumberInput("Базовый HP:", 1, 500, (v) => cbs.onSetMonsterBaseHp?.(v));

  // Mode selector
  const modeRow = document.createElement("div");
  modeRow.style.cssText = "margin-bottom:10px";
  const modeLbl = document.createElement("div");
  modeLbl.style.cssText = "color:#8a7a5a;font-size:12px;margin-bottom:4px";
  modeLbl.textContent = "Режим:";
  modeRow.appendChild(modeLbl);

  const modeWpBtn = document.createElement("button");
  modeWpBtn.textContent = "Путь [W]";
  modeWpBtn.style.cssText = "margin-right:4px;padding:2px 8px;cursor:pointer;font-size:11px;background:#22aa44;border:1px solid #3a2820;color:#fff;border-radius:3px";
  modeWpBtn.addEventListener("click", () => cbs.onSetMode?.("waypoint"));
  modeRow.appendChild(modeWpBtn);

  const modeBlkBtn = document.createElement("button");
  modeBlkBtn.textContent = "Блок [B]";
  modeBlkBtn.style.cssText = "padding:2px 8px;cursor:pointer;font-size:11px;background:#0a0806;border:1px solid #3a2820;color:#e8dcb4;border-radius:3px";
  modeBlkBtn.addEventListener("click", () => cbs.onSetMode?.("block"));
  modeRow.appendChild(modeBlkBtn);
  el.appendChild(modeRow);

  // Divider
  const div1 = document.createElement("hr");
  div1.style.cssText = "border:none;border-top:1px solid #3a2820;margin:8px 0";
  el.appendChild(div1);

  // Waypoint buttons
  function makeBtn(text: string, onClick: () => void, color = "#0a0806"): void {
    const btn = document.createElement("button");
    btn.textContent = text;
    btn.style.cssText = `width:100%;margin-bottom:4px;padding:4px;cursor:pointer;background:${color};border:1px solid #3a2820;color:#e8dcb4;border-radius:3px;font-size:12px`;
    btn.addEventListener("click", onClick);
    el.appendChild(btn);
  }

  makeBtn("Очистить путь [C]", () => cbs.onClearWaypoints?.());
  makeBtn("Убрать посл. точку [Z]", () => cbs.onRemoveLastWaypoint?.());

  // Divider
  const div2 = document.createElement("hr");
  div2.style.cssText = "border:none;border-top:1px solid #3a2820;margin:8px 0";
  el.appendChild(div2);

  makeBtn("📋 Экспорт YAML", () => cbs.onExportYaml?.(), "#1a3a4a");
  makeBtn("✓ Сохранить [S]", () => cbs.onSave?.(), "#1a4a1a");
  makeBtn("✗ Отмена [Esc]", () => cbs.onCancel?.(), "#4a1a1a");
  makeBtn("⟳ Сброс оверрайда", () => cbs.onResetOverride?.());

  function update(state: GameStateView): void {
    const isEditor = state.scene === "editor";
    el.style.display = isEditor ? "" : "none";
    if (!isEditor || !state.editor) return;

    const ed = state.editor;
    // Update inputs only if not focused to avoid fighting with user input
    if (document.activeElement !== wavesInput) wavesInput.value = String(ed.wavesNum);
    if (document.activeElement !== monstersInput) monstersInput.value = String(ed.monstersPerWave);
    if (document.activeElement !== hpInput) hpInput.value = String(ed.monsterBaseHp);

    // Update mode buttons
    if (ed.mode === "waypoint") {
      modeWpBtn.style.background = "#22aa44";
      modeBlkBtn.style.background = "#0a0806";
    } else {
      modeWpBtn.style.background = "#0a0806";
      modeBlkBtn.style.background = "#aa4422";
    }
  }

  function destroy(): void {
    el.remove();
  }

  return { update, destroy };
}
